import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Database, ArrowRight, CheckCircle2, XCircle, Plus, Loader2, 
  Sparkles, Play, Shield, FileCheck, AlertTriangle, TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DataMigrationPlanner = ({ project }) => {
  const [activeTab, setActiveTab] = useState('objects');
  const [sourceObjects, setSourceObjects] = useState([]);
  const [targetObjects, setTargetObjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [validationRules, setValidationRules] = useState([]);
  const [migrationTests, setMigrationTests] = useState([]);
  const [migrationRuns, setMigrationRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateObject, setShowCreateObject] = useState(false);
  const [showCreateMapping, setShowCreateMapping] = useState(false);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  useEffect(() => {
    if (selectedMapping) {
      fetchValidationRules(selectedMapping.id);
      fetchMigrationTests(selectedMapping.id);
    }
  }, [selectedMapping]);

  const fetchData = async () => {
    try {
      const [sourceResp, targetResp, mappingsResp, statsResp, runsResp] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/data-objects?system=source`),
        axios.get(`${API}/projects/${project.id}/data-objects?system=target`),
        axios.get(`${API}/projects/${project.id}/data-mappings`),
        axios.get(`${API}/projects/${project.id}/migration-stats`),
        axios.get(`${API}/projects/${project.id}/migration-runs`)
      ]);
      
      setSourceObjects(sourceResp.data);
      setTargetObjects(targetResp.data);
      setMappings(mappingsResp.data);
      setStats(statsResp.data);
      setMigrationRuns(runsResp.data);
      
      if (mappingsResp.data.length > 0 && !selectedMapping) {
        setSelectedMapping(mappingsResp.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchValidationRules = async (mappingId) => {
    try {
      const response = await axios.get(`${API}/data-mappings/${mappingId}/validation-rules`);
      setValidationRules(response.data);
    } catch (error) {
      console.error('Error fetching validation rules:', error);
    }
  };

  const fetchMigrationTests = async (mappingId) => {
    try {
      const response = await axios.get(`${API}/data-mappings/${mappingId}/tests`);
      setMigrationTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const createDataObject = async (objectData) => {
    try {
      const newObject = {
        ...objectData,
        project_id: project.id,
      };
      await axios.post(`${API}/data-objects`, newObject);
      await fetchData();
      setShowCreateObject(false);
    } catch (error) {
      console.error('Error creating object:', error);
    }
  };

  const createMapping = async (mappingData) => {
    try {
      const newMapping = {
        ...mappingData,
        project_id: project.id,
      };
      await axios.post(`${API}/data-mappings`, newMapping);
      await fetchData();
      setShowCreateMapping(false);
    } catch (error) {
      console.error('Error creating mapping:', error);
    }
  };

  const suggestMappings = async () => {
    setSuggesting(true);
    try {
      await axios.post(`${API}/data-mappings/ai-suggest?project_id=${project.id}`);
      await fetchData();
    } catch (error) {
      console.error('Error suggesting mappings:', error);
    } finally {
      setSuggesting(false);
    }
  };

  const createValidationRule = async (ruleData) => {
    try {
      const newRule = {
        ...ruleData,
        project_id: project.id,
        mapping_id: selectedMapping.id,
      };
      await axios.post(`${API}/validation-rules`, newRule);
      await fetchValidationRules(selectedMapping.id);
      setShowCreateRule(false);
    } catch (error) {
      console.error('Error creating rule:', error);
    }
  };

  const executeTest = async (testId) => {
    try {
      await axios.post(`${API}/migration-tests/${testId}/execute`);
      await fetchMigrationTests(selectedMapping.id);
    } catch (error) {
      console.error('Error executing test:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'mapped': case 'approved': case 'passed': case 'completed':
        return 'bg-green-100 text-green-700';
      case 'validated': case 'running':
        return 'bg-blue-100 text-blue-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Data Migration Planner</h2>
            <p className="text-gray-600">Map source objects to target, define validation rules, and test migrations</p>
          </div>
          <button
            onClick={() => setShowCreateObject(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Data Object</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Database className="w-10 h-10 opacity-80" />
              <span className="text-3xl font-bold">{stats.total_source_objects}</span>
            </div>
            <p className="text-blue-100 text-sm font-medium">Source Objects</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Database className="w-10 h-10 opacity-80" />
              <span className="text-3xl font-bold">{stats.total_target_objects}</span>
            </div>
            <p className="text-purple-100 text-sm font-medium">Target Objects</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <ArrowRight className="w-10 h-10 opacity-80" />
              <span className="text-3xl font-bold">{stats.total_mappings}</span>
            </div>
            <p className="text-green-100 text-sm font-medium">Mappings Created</p>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-10 h-10 opacity-80" />
              <span className="text-3xl font-bold">{stats.mapping_coverage}%</span>
            </div>
            <p className="text-orange-100 text-sm font-medium">Coverage</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {['objects', 'mappings', 'validation', 'testing'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-6 py-4 font-medium transition-all ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab === 'objects' && '📦 Data Objects'}
              {tab === 'mappings' && '🔗 Object Mappings'}
              {tab === 'validation' && '✓ Validation Rules'}
              {tab === 'testing' && '🧪 Migration Testing'}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'objects' && (
            <ObjectsTab
              sourceObjects={sourceObjects}
              targetObjects={targetObjects}
              onRefresh={fetchData}
            />
          )}
          
          {activeTab === 'mappings' && (
            <MappingsTab
              sourceObjects={sourceObjects}
              targetObjects={targetObjects}
              mappings={mappings}
              onCreateMapping={() => setShowCreateMapping(true)}
              onSuggestMappings={suggestMappings}
              suggesting={suggesting}
              onSelectMapping={setSelectedMapping}
              selectedMapping={selectedMapping}
            />
          )}
          
          {activeTab === 'validation' && selectedMapping && (
            <ValidationTab
              mapping={selectedMapping}
              validationRules={validationRules}
              onCreateRule={() => setShowCreateRule(true)}
              onRefresh={() => fetchValidationRules(selectedMapping.id)}
            />
          )}
          
          {activeTab === 'testing' && selectedMapping && (
            <TestingTab
              mapping={selectedMapping}
              migrationTests={migrationTests}
              onExecuteTest={executeTest}
              onRefresh={() => fetchMigrationTests(selectedMapping.id)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateObject && (
        <CreateObjectModal
          onClose={() => setShowCreateObject(false)}
          onCreate={createDataObject}
        />
      )}
      
      {showCreateMapping && (
        <CreateMappingModal
          onClose={() => setShowCreateMapping(false)}
          onCreate={createMapping}
          sourceObjects={sourceObjects}
          targetObjects={targetObjects}
        />
      )}
      
      {showCreateRule && selectedMapping && (
        <CreateRuleModal
          onClose={() => setShowCreateRule(false)}
          onCreate={createValidationRule}
        />
      )}
    </div>
  );
};

// Objects Tab
const ObjectsTab = ({ sourceObjects, targetObjects }) => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Source Objects (ECC)</h3>
        {sourceObjects.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No source objects yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {sourceObjects.map((obj) => (
              <div key={obj.id} className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{obj.object_name}</h4>
                    <p className="text-sm text-gray-600">{obj.technical_name}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(obj.migration_status)}`}>
                    {obj.migration_status}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>Type: {obj.object_type}</span>
                  <span>Records: {obj.record_count.toLocaleString()}</span>
                  <span>Fields: {obj.fields.length}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Target Objects (S/4HANA)</h3>
        {targetObjects.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No target objects yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {targetObjects.map((obj) => (
              <div key={obj.id} className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{obj.object_name}</h4>
                    <p className="text-sm text-gray-600">{obj.technical_name}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(obj.migration_status)}`}>
                    {obj.migration_status}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>Type: {obj.object_type}</span>
                  <span>Fields: {obj.fields.length}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

// Mappings Tab
const MappingsTab = ({ sourceObjects, targetObjects, mappings, onCreateMapping, onSuggestMappings, suggesting, onSelectMapping, selectedMapping }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-bold text-gray-900">Object Mappings</h3>
      <div className="flex space-x-3">
        <button
          onClick={onSuggestMappings}
          disabled={suggesting}
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
        >
          {suggesting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Suggesting...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>AI Suggest</span>
            </>
          )}
        </button>
        <button
          onClick={onCreateMapping}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Mapping</span>
        </button>
      </div>
    </div>

    {mappings.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <ArrowRight className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600 mb-4">No mappings yet. Use AI to suggest or create manually.</p>
      </div>
    ) : (
      <div className="space-y-3">
        {mappings.map((mapping) => {
          const sourceObj = sourceObjects.find(o => o.id === mapping.source_object_id);
          const targetObj = targetObjects.find(o => o.id === mapping.target_object_id);
          
          return (
            <div
              key={mapping.id}
              onClick={() => onSelectMapping(mapping)}
              className={`border-2 rounded-lg p-5 cursor-pointer transition-all ${
                selectedMapping?.id === mapping.id
                  ? 'border-blue-500 bg-blue-50 shadow-lg'
                  : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="bg-blue-100 px-4 py-2 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900">{sourceObj?.object_name || 'Unknown'}</p>
                    <p className="text-xs text-blue-600">{sourceObj?.technical_name}</p>
                  </div>
                  
                  <ArrowRight className="w-6 h-6 text-gray-400" />
                  
                  <div className="bg-purple-100 px-4 py-2 rounded-lg">
                    <p className="text-sm font-semibold text-purple-900">{targetObj?.object_name || 'Unknown'}</p>
                    <p className="text-xs text-purple-600">{targetObj?.technical_name}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {mapping.ai_suggested && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-semibold flex items-center space-x-1">
                      <Sparkles className="w-3 h-3" />
                      <span>AI {Math.round(mapping.confidence_score * 100)}%</span>
                    </span>
                  )}
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(mapping.status)}`}>
                    {mapping.status}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <span className="capitalize px-2 py-1 bg-gray-100 rounded">
                  {mapping.mapping_type.replace('_', ' ')}
                </span>
                <span>{mapping.field_mappings.length} field mappings</span>
                {mapping.transformation_logic && (
                  <span className="text-orange-600 font-semibold">Has transformation</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// Validation Tab
const ValidationTab = ({ mapping, validationRules, onCreateRule, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-bold text-gray-900">Validation Rules for Selected Mapping</h3>
      <button
        onClick={onCreateRule}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
      >
        <Plus className="w-4 h-4" />
        <span>Add Rule</span>
      </button>
    </div>

    {validationRules.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600">No validation rules defined yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {validationRules.map((rule) => (
          <div key={rule.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-semibold text-gray-900">{rule.rule_name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    rule.severity === 'error' ? 'bg-red-100 text-red-700' :
                    rule.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {rule.severity}
                  </span>
                  <span className="capitalize px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                    {rule.rule_type.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-800">
                  {rule.validation_logic}
                </code>
              </div>
              <div className="ml-4">
                <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                  rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {rule.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Testing Tab
const TestingTab = ({ mapping, migrationTests, onExecuteTest, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-bold text-gray-900">Migration Tests</h3>
      <div className="text-sm text-gray-600">
        {migrationTests.filter(t => t.status === 'passed').length} / {migrationTests.length} passed
      </div>
    </div>

    {migrationTests.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <FileCheck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600">No tests created yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {migrationTests.map((test) => (
          <div key={test.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-semibold text-gray-900">{test.test_name}</h4>
                  <span className="capitalize px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                    {test.test_type}
                  </span>
                </div>
                {test.status === 'passed' && (
                  <div className="flex items-center space-x-2 text-green-600 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Test passed in {test.execution_time_ms}ms</span>
                  </div>
                )}
                {test.status === 'failed' && (
                  <div className="flex items-center space-x-2 text-red-600 text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>Test failed: {test.error_message}</span>
                  </div>
                )}
                {test.status === 'pending' && (
                  <p className="text-sm text-gray-600">Ready to execute</p>
                )}
              </div>
              <div className="ml-4 flex flex-col items-end space-y-2">
                <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${getStatusColor(test.status)}`}>
                  {test.status}
                </span>
                {test.status === 'pending' && (
                  <button
                    onClick={() => onExecuteTest(test.id)}
                    className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1 text-xs"
                  >
                    <Play className="w-3 h-3" />
                    <span>Execute</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Create Object Modal
const CreateObjectModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    object_name: '',
    object_type: 'table',
    system: 'source',
    technical_name: '',
    description: '',
    record_count: 0,
    fields: [],
    is_custom: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Add Data Object</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Object Name *</label>
              <input
                type="text"
                required
                value={formData.object_name}
                onChange={(e) => setFormData({ ...formData, object_name: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Material Master"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Technical Name *</label>
              <input
                type="text"
                required
                value={formData.technical_name}
                onChange={(e) => setFormData({ ...formData, technical_name: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., MARA"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Object Type</label>
              <select
                value={formData.object_type}
                onChange={(e) => setFormData({ ...formData, object_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="table">Table</option>
                <option value="view">View</option>
                <option value="structure">Structure</option>
                <option value="custom_object">Custom Object</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">System</label>
              <select
                value={formData.system}
                onChange={(e) => setFormData({ ...formData, system: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="source">Source (ECC)</option>
                <option value="target">Target (S/4HANA)</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Record Count</label>
            <input
              type="number"
              value={formData.record_count}
              onChange={(e) => setFormData({ ...formData, record_count: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_custom"
              checked={formData.is_custom}
              onChange={(e) => setFormData({ ...formData, is_custom: e.target.checked })}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="is_custom" className="text-sm font-semibold text-gray-700">
              Custom Object (Z* or Y*)
            </label>
          </div>
          
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Object
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Mapping Modal
const CreateMappingModal = ({ onClose, onCreate, sourceObjects, targetObjects }) => {
  const [formData, setFormData] = useState({
    source_object_id: '',
    target_object_id: '',
    mapping_type: 'one_to_one',
    field_mappings: [],
    transformation_logic: '',
    status: 'draft',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Data Mapping</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Source Object *</label>
            <select
              required
              value={formData.source_object_id}
              onChange={(e) => setFormData({ ...formData, source_object_id: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select source object</option>
              {sourceObjects.map((obj) => (
                <option key={obj.id} value={obj.id}>
                  {obj.object_name} ({obj.technical_name}) - {obj.record_count.toLocaleString()} records
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Target Object *</label>
            <select
              required
              value={formData.target_object_id}
              onChange={(e) => setFormData({ ...formData, target_object_id: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select target object</option>
              {targetObjects.map((obj) => (
                <option key={obj.id} value={obj.id}>
                  {obj.object_name} ({obj.technical_name})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Mapping Type</label>
            <select
              value={formData.mapping_type}
              onChange={(e) => setFormData({ ...formData, mapping_type: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="one_to_one">One-to-One</option>
              <option value="one_to_many">One-to-Many</option>
              <option value="many_to_one">Many-to-One</option>
              <option value="transformation">Transformation</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Transformation Logic (if applicable)</label>
            <textarea
              value={formData.transformation_logic}
              onChange={(e) => setFormData({ ...formData, transformation_logic: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="SQL or Python transformation logic"
            />
          </div>
          
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Mapping
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Rule Modal
const CreateRuleModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    rule_name: '',
    rule_type: 'data_type',
    description: '',
    validation_logic: '',
    severity: 'error',
    is_active: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Validation Rule</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Rule Name *</label>
            <input
              type="text"
              required
              value={formData.rule_name}
              onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Check Material Number Format"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Rule Type</label>
              <select
                value={formData.rule_type}
                onChange={(e) => setFormData({ ...formData, rule_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="data_type">Data Type Validation</option>
                <option value="range">Range Check</option>
                <option value="format">Format Validation</option>
                <option value="mandatory">Mandatory Field</option>
                <option value="uniqueness">Uniqueness Check</option>
                <option value="referential_integrity">Referential Integrity</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="error">Error (Must Fix)</option>
                <option value="warning">Warning (Review)</option>
                <option value="info">Info (FYI)</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
              placeholder="Describe what this rule validates"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Validation Logic *</label>
            <textarea
              required
              value={formData.validation_logic}
              onChange={(e) => setFormData({ ...formData, validation_logic: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              rows={3}
              placeholder="e.g., LENGTH(field) = 18 AND field LIKE 'MAT%'"
            />
          </div>
          
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Rule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'mapped': case 'approved': case 'passed': case 'completed':
      return 'bg-green-100 text-green-700';
    case 'validated': case 'running': case 'reviewed':
      return 'bg-blue-100 text-blue-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export default DataMigrationPlanner;
